import { useState, useEffect, useRef, useCallback } from "react";
import tw from "tailwind-styled-components";
import { useMutation, useQuery } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import NotFoundModel from "../../Components/NotFoundModel";
import { useSelector } from "react-redux";
import SideModal from "../../Components/SideModal";
import { toast } from "react-toastify";
import DeleteCommissionModal from "../../Components/StaffCommission/DeleteCommissionModal";
import MarkAsPaidStaffCommission from "../../Components/StaffCommission/MarkAsPaidStaffCommission";
import EditCommission from "../../Components/StaffCommission/EditCommission";
import EditExpenses from "../../Components/Expenses/EditExpenses";
import Table from "./StaffDetailCommissionTable";
import moment from "moment";
import TenantTable from "../../Components/Tenants/Table";
import { useNavigate } from "react-router-dom";

const StaffDetailCommission = ({ staffId }) => {
    const user = useSelector((state) => state.UserReducer.user);
    const navigate = useNavigate();

    const properties = useSelector((state) => state.PropertyReducer.property);
    const [activeTab, setActiveTab] = useState("3");
    const [selectedData, setSelectedData] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [apiData, setApiData] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [isDeleteCommissionModal, setIsDeleteCommissionModal] = useState(false);
    const [isEditCommissionModal, setIsEditCommissionModal] = useState(false);
    const [isPayCommissionModal, setIsPayCommissionModal] = useState(false);
    const [expenseData, setExpenseData] = useState([]);
    const [isEditExpense, setIsEditExpense] = useState(false);
    const endDate = moment().format("YYYY-MM-DD");
    const startDate = moment().subtract(1, "year").format("YYYY-MM-DD");

    const hasMounted = useRef(false);
    const initialLoadDone = useRef(false);

    // const buildQueryParams = useCallback(() => {
    //     if (!staffId) return "";
    //     const params = new URLSearchParams();
    //     params.append('pageNum', '1');
    //     params.append('staffFilters', staffId);
    //     params.append('isPaid', activeTab === "2" ? "1" : "0");

    //     return params.toString();
    // }, [activeTab, staffId]);

    const buildQueryParams = useCallback(() => {
        if (!staffId) return "";

        const params = new URLSearchParams();
        params.append("pageNum", "1");

        if (activeTab === "3") {
            params.append("bookedBy", staffId);
            params.append("startDate", startDate);
            params.append("endDate", endDate);
        } else {
            params.append("staffFilters", staffId);
            params.append("isPaid", activeTab === "2" ? "1" : "0");
        }

        return params.toString();
    }, [activeTab, staffId, startDate, endDate]);

    const fetchFunction = useCallback(async () => {
        const query = buildQueryParams();

        const route =
            activeTab === "3"
                ? "staff/list/booked/tenant"
                : "staff-commission/list";

        const response = await axios.get(
            `${Config.apiUrl}/${route}?${query}`,
            {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    }, [activeTab, buildQueryParams, user.token]);

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            // Handle error
        } else {
            setApiData(res?.data);
            setStaffList(res?.staffs || []);
            setIsFocused(true);
        }
    };

    const getError = (res) => {
        //console.error("Error fetching transactions:", res);
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        if (hasMounted.current) return;
        hasMounted.current = true;

        const timer = setTimeout(() => {
            if (!initialLoadDone.current) {
                getListMutate();
                initialLoadDone.current = true;
            }
        }, 50);

        return () => clearTimeout(timer);
    }, [getListMutate]);

    useEffect(() => {
        if (!hasMounted.current || !initialLoadDone.current) return;

        const timer = setTimeout(() => {
            getListMutate();
        }, 100);
        return () => clearTimeout(timer);
    }, [activeTab, getListMutate]);

    const handleTabChange = (tab) => {
        setApiData([]);
        setActiveTab(tab);
    };

    //------- Commission Delete Modal -------
    const {
        isLoading: deleteCommissionLoading,
        mutate: deleteCommissionMutate,
    } = useMutation(
        (payload) =>
            axios.post(`${Config.apiUrl}/staff-commission/delete`, payload, {
                headers: {
                    authorization: `Bearer ${user.token}`,
                },
            }),
        {
            onSettled: () => {
                setIsDeleteCommissionModal(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );

    //------- Commission Edit Modal -------
    const {
        isLoading: editCommissionLoading,
        mutate: editCommissionMutate,
    } = useMutation(
        (payload) =>
            axios.post(`${Config.apiUrl}/staff-commission/edit`, payload, {
                headers: {
                    authorization: `Bearer ${user.token}`,
                },
            }),
        {
            onSettled: () => {
                setIsEditCommissionModal(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );

    //------- Commission Pay Modal -------
    const {
        isLoading: payCommissionLoading,
        mutate: payCommissionMutate,
    } = useMutation(
        (payload) =>
            axios.post(`${Config.apiUrl}/staff-commission/pay`, payload, {
                headers: {
                    authorization: `Bearer ${user.token}`,
                },
            }),
        {
            onSettled: () => {
                setIsPayCommissionModal(false);
            },
            onSuccess: ({ data }) => {
                getListMutate();
                toast.success(data?.msg || 'Success');
            },
            onError: (error) => {
                toast.error(error?.response?.data?.msg || 'An error occurred');
            },
        }
    );

    const fetchListing = async () =>
        await axios.get(`${Config.apiUrl}/expense/initial-values`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
            },
        });

    const { isLoading: isExpensesLoading, error: expensesError, data: expensesData, refetch, isRefetching: isExpensesRefetching } = useQuery(
        ['expenseData',],
        fetchListing,
        {
            enabled: isEditExpense,
            onSuccess: (response) => {
                setExpenseData(response?.data?.data);
            },
            onError: (error) => {
                console.error("Error fetching expense data:", error);
            }
        }
    );

    const categories = expenseData?.expenseCategories?.map((section) => ({
        type: "group",
        name: section.name,
        items: section.items.map((item) => ({
            value: item.id + "",
            label: item.name,
        })),
    }));


    //------- Edit Expense -------
    const editExpenseFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/expense/edit`, values, {
            headers: {
                Authorization: `Bearer ${user.token}`,
                "Content-Type": "application/x-www-form-urlenced; charset=UTF-8",
            },
        });
    };

    const onEditExpenseSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsEditExpense(false);
        } else {
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onEditExpenseError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: editExpenseLoading, mutate: editExpenseMutate } =
        useMutation(editExpenseFunction, {
            onSuccess: onEditExpenseSuccess,
            onError: onEditExpenseError,
        });

    const handleEditCommissionModal = () => {
        setIsEditCommissionModal(false);
    };

    const handlePayCommissionModal = () => {
        setIsPayCommissionModal(false);
    };

    const handleExpansesClose = () => {
        setIsEditExpense(false);
    };
    return (
        <>
            <SideModal
                isOpen={isEditCommissionModal}
                onClose={() => setIsEditCommissionModal(false)}
            >
                <EditCommission
                    setIsEditCommissionModal={setIsEditCommissionModal}
                    isEditCommissionModal={isEditCommissionModal}
                    handleClose={handleEditCommissionModal}
                    selectedData={selectedData}
                    editCommissionLoading={editCommissionLoading}
                    editCommissionMutate={editCommissionMutate}
                    staffList={staffList}
                />
            </SideModal>

            <SideModal
                isOpen={isPayCommissionModal}
                onClose={() => setIsPayCommissionModal(false)}
            >
                <MarkAsPaidStaffCommission
                    isPayCommissionModal={isPayCommissionModal}
                    setIsPayCommissionModal={setIsPayCommissionModal}
                    handleClose={handlePayCommissionModal}
                    payCommissionLoading={payCommissionLoading}
                    payCommissionMutate={payCommissionMutate}
                    selectedStaff={selectedData}

                />
            </SideModal>

            {isDeleteCommissionModal && (
                <DeleteCommissionModal
                    setIsDeleteCommissionModal={setIsDeleteCommissionModal}
                    deleteCommissionLoading={deleteCommissionLoading}
                    deleteCommissionMutate={deleteCommissionMutate}
                    selectedData={selectedData}
                />
            )}

            <SideModal isOpen={isEditExpense} onClose={() => setIsEditExpense(false)}
            >
                <EditExpenses
                    setIsEditExpense={setIsEditExpense}
                    isEditExpense={isEditExpense}
                    handleClose={handleExpansesClose}
                    editExpenseLoading={editExpenseLoading}
                    editExpenseMutate={editExpenseMutate}
                    selectedData={selectedData}
                    expenseData={expenseData}
                    categories={categories}
                    property={properties}
                />
            </SideModal>
            <div className="space-y-4">
                <FloorTabs>
                    <FloorTab
                        key="1"
                        onClick={() => handleTabChange('3')}
                        $active={activeTab === "3"}
                    >
                        Bookings
                    </FloorTab>
                    <FloorTab
                        key="2"
                        onClick={() => handleTabChange('1')}
                        $active={activeTab === "1"}
                    >
                        Unpaid Commission
                        {/* (₹{data?.unpaidTotal || 0}) */}
                    </FloorTab>
                    <FloorTab
                        key="3"
                        onClick={() => handleTabChange('2')}
                        $active={activeTab === "2"}
                    >
                        Paid Commission
                        {/* (₹{data?.paidTotal || 0}) */}
                    </FloorTab>
                </FloorTabs>

                {!error && !isLoading && (
                    <TableWrapper>
                        {activeTab === "3" ? (
                            <TenantTable
                                //apiData={error ? [] : apiData?.length > 0 ? apiData : []}
                                apiData={error ? [] : apiData}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                                isMovingOut={false}
                                setIsViewNotes={() => { }}
                                handleOpenTenantModal={(tenantId) => {
                                    navigate(`/tenantdetails/l/${tenantId}`, {
                                        state: {
                                            tabName: 'Tenant Info',
                                            tabKey: "totalDues",
                                        },
                                    });
                                }}
                            />
                        ) : (
                            <Table
                                apiData={apiData?.length > 0 ? apiData : []}
                                setSelectedData={setSelectedData}
                                selectedData={selectedData}
                                setIsDeleteCommissionModal={setIsDeleteCommissionModal}
                                setIsEditCommissionModal={setIsEditCommissionModal}
                                setIsPayCommissionModal={setIsPayCommissionModal}
                                setIsEditExpense={setIsEditExpense}
                            />
                        )}

                        {(!apiData || apiData.length === 0) && <NotFoundModel />}
                    </TableWrapper>
                )}

                {error && !isLoading && <NotFoundModel />}
                {(isLoading || isRefetching) && <Loading />}

            </div>
        </>
    );
};

const TableWrapper = tw.div`border bg-white rounded-md`;
const FloorTabs = tw.div`flex overflow-x-auto bg-white rounded-md my-4 border-b`;
const FloorTab = tw.button`
  ${(p) =>
        p.$active
            ? " text-primary-color border-b-2 border-b-primary-color bg-cyan-50 "
            : " text-gray-700 "} 
   px-5 py-3  cursor-pointer font-medium transition-all duration-200 whitespace-nowrap text-sm
`;
export default StaffDetailCommission;
